/*
  KAPSULE Global JavaScript Utilities
  Shared helper functions for all Shogun snippets

  Usage: Include this once before any snippet JavaScript
*/

window.KAPSULE = window.KAPSULE || {};

(function() {
  'use strict';

  /* ========================================
     Price Formatting
     ======================================== */
  KAPSULE.formatPrice = function(cents) {
    const dollars = cents / 100;
    return `$${dollars.toFixed(2)}`;
  };

  KAPSULE.formatPriceShort = function(cents) {
    const dollars = Math.round(cents / 100);
    return `$${dollars}`;
  };

  /* ========================================
     Cart API Helpers
     ======================================== */

  /**
   * Get the cart UI element (drawer or notification)
   */
  KAPSULE.getCartUI = function() {
    return document.querySelector('cart-drawer') ||
           document.querySelector('cart-notification');
  };

  /**
   * Get section IDs for cart rendering
   */
  KAPSULE.getSectionIds = function() {
    const ui = KAPSULE.getCartUI();
    if (ui && typeof ui.getSectionsToRender === 'function') {
      return ui.getSectionsToRender().map(s => s.section || s.id);
    }
    return ['cart-drawer', 'cart-icon-bubble', 'cart-live-region-text'];
  };

  /**
   * Refresh cart UI after adding items
   */
  KAPSULE.refreshCartUI = async function() {
    const ui = KAPSULE.getCartUI();
    const ids = KAPSULE.getSectionIds();

    const sectionsUrl = window.location.pathname +
                       '?sections=' +
                       encodeURIComponent(ids.join(','));

    const res = await fetch(sectionsUrl, {
      credentials: 'same-origin'
    });

    if (!res.ok) {
      throw new Error('Sections fetch failed (' + res.status + ')');
    }

    const sectionsJson = await res.json();
    const parsedState = { sections: sectionsJson };

    if (ui && typeof ui.renderContents === 'function') {
      ui.classList.remove('is-empty');
      ui.renderContents(parsedState);
    }
  };

  /**
   * Open cart drawer
   */
  KAPSULE.openCartDrawer = function() {
    const ui = KAPSULE.getCartUI();
    if (ui && typeof ui.open === 'function') {
      ui.open();
    }
  };

  /**
   * Add items to cart
   * @param {Array} items - Array of line items
   * @param {Object} options - Optional settings
   * @returns {Promise<Object>} - Cart response
   */
  KAPSULE.addToCart = async function(items, options = {}) {
    const {
      openDrawer = true,
      refreshUI = true,
      onSuccess = null,
      onError = null
    } = options;

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({ items })
      });

      const text = await res.text();
      let json = null;
      try {
        json = JSON.parse(text);
      } catch(e) {
        console.warn('[KAPSULE] Could not parse cart response:', e);
      }

      if (!res.ok) {
        const error = new Error(
          json?.description ||
          json?.message ||
          'Add to cart failed (' + res.status + ')'
        );
        error.status = res.status;
        error.response = json;
        throw error;
      }

      if (refreshUI) {
        await KAPSULE.refreshCartUI();
      }

      if (openDrawer) {
        KAPSULE.openCartDrawer();
      }

      if (onSuccess) {
        onSuccess(json);
      }

      return json;

    } catch (err) {
      console.error('[KAPSULE] addToCart error:', err);

      if (onError) {
        onError(err);
      } else {
        // Default error handling
        alert('Could not add to cart. Please try again.');
      }

      throw err;
    }
  };

  /* ========================================
     Line Item Builders
     ======================================== */

  /**
   * Create a cart line item object
   * @param {Object} config - Line item configuration
   * @returns {Object} - Formatted line item
   */
  KAPSULE.createLineItem = function(config) {
    const {
      variantId,
      quantity = 1,
      mode = 'onetime', // 'onetime' or 'subscription'
      sellingPlanId = null,
      properties = {}
    } = config;

    const line = {
      id: Number(variantId),
      quantity: Number(quantity),
      properties: {
        _kapsule_mode: mode,
        ...properties
      }
    };

    if (mode === 'subscription' && sellingPlanId) {
      line.selling_plan = Number(sellingPlanId);
    }

    return line;
  };

  /* ========================================
     DOM Utilities
     ======================================== */

  /**
   * Check if device is mobile based on screen width
   */
  KAPSULE.isMobile = function() {
    return window.matchMedia('(max-width: 767px)').matches;
  };

  /**
   * Check if device is tablet
   */
  KAPSULE.isTablet = function() {
    return window.matchMedia('(min-width: 768px) and (max-width: 991px)').matches;
  };

  /**
   * Check if device is desktop
   */
  KAPSULE.isDesktop = function() {
    return window.matchMedia('(min-width: 992px)').matches;
  };

  /**
   * Debounce function calls
   */
  KAPSULE.debounce = function(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  /**
   * Throttle function calls
   */
  KAPSULE.throttle = function(func, limit) {
    let inThrottle;
    return function(...args) {
      if (!inThrottle) {
        func.apply(this, args);
        inThrottle = true;
        setTimeout(() => inThrottle = false, limit);
      }
    };
  };

  /* ========================================
     State Management Helpers
     ======================================== */

  /**
   * Simple event emitter for snippet communication
   */
  KAPSULE.events = {
    listeners: {},

    on(event, callback) {
      if (!this.listeners[event]) {
        this.listeners[event] = [];
      }
      this.listeners[event].push(callback);
    },

    off(event, callback) {
      if (!this.listeners[event]) return;
      this.listeners[event] = this.listeners[event].filter(
        cb => cb !== callback
      );
    },

    emit(event, data) {
      if (!this.listeners[event]) return;
      this.listeners[event].forEach(callback => {
        try {
          callback(data);
        } catch (err) {
          console.error('[KAPSULE] Event handler error:', err);
        }
      });
    }
  };

  /* ========================================
     Validation Helpers
     ======================================== */

  /**
   * Validate that all required items are selected
   */
  KAPSULE.validateMinimum = function(count, minimum, itemName = 'item') {
    const remaining = minimum - count;
    if (remaining > 0) {
      return {
        valid: false,
        message: `Please select ${remaining} more ${itemName}${remaining === 1 ? '' : 's'}`,
        remaining
      };
    }
    return {
      valid: true,
      message: 'Ready to add to cart',
      remaining: 0
    };
  };

  /* ========================================
     Analytics Helpers
     ======================================== */

  /**
   * Track custom events (works with GA4, FB Pixel, etc.)
   */
  KAPSULE.trackEvent = function(eventName, eventData = {}) {
    // Google Analytics 4
    if (window.gtag) {
      gtag('event', eventName, eventData);
    }

    // Facebook Pixel
    if (window.fbq) {
      fbq('trackCustom', eventName, eventData);
    }

    // Custom dataLayer push
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      ...eventData
    });

    console.log('[KAPSULE] Event tracked:', eventName, eventData);
  };

  /* ========================================
     Loading States
     ======================================== */

  /**
   * Set button loading state
   */
  KAPSULE.setButtonLoading = function(button, loading, loadingText = 'Loading...') {
    if (!button) return;

    if (loading) {
      button.dataset.originalText = button.textContent;
      button.textContent = loadingText;
      button.disabled = true;
      button.classList.add('is-loading');
    } else {
      button.textContent = button.dataset.originalText || button.textContent;
      button.disabled = false;
      button.classList.remove('is-loading');
      delete button.dataset.originalText;
    }
  };

  /* ========================================
     Error Handling
     ======================================== */

  /**
   * Display user-friendly error messages
   */
  KAPSULE.showError = function(message, options = {}) {
    const {
      duration = 5000,
      type = 'error' // 'error', 'warning', 'info'
    } = options;

    // Use toast notification if available
    if (window.showToast) {
      window.showToast(message, type, duration);
      return;
    }

    // Fallback to alert
    alert(message);
  };

  /**
   * Log errors consistently
   */
  KAPSULE.logError = function(context, error) {
    console.group('[KAPSULE] Error in ' + context);
    console.error('Message:', error.message);
    console.error('Stack:', error.stack);
    if (error.response) {
      console.error('Response:', error.response);
    }
    console.groupEnd();
  };

  /* ========================================
     Initialize
     ======================================== */
  console.log('[KAPSULE] Global utilities loaded');

})();
